import type { RefCallback } from "react"
import { MEDIA_QUERIES } from "@/constants/ui"

const DURATION_MS = 420
// Shorter than one bar's run, so a trough crosses the waveform instead of the
// whole icon emptying at once.
const STAGGER_MS = 85
const TROUGH_OFFSET = 0.45

// Runs one ripple across the audio-lines waveform inside `node`. Driven from
// rect.animate() rather than a :hover rule so a ripple that has started always
// finishes, and it reads its geometry off the rendered bars so it never has to
// know the waveform's shape. Shared by the voice-mode button's hover and the
// voice orb's live flourish, so the waveform moves the same way wherever it is.
export function rippleAudioLines(node: Element) {
  // Read at the moment of the trigger, so a preference changed mid-session is
  // honoured without subscribing.
  if (window.matchMedia(MEDIA_QUERIES.reducedMotion).matches) {
    return
  }
  // Only a capsule can collapse to a dot, so the bars are the rects whose
  // corner radius is half their width. The other rect-built icons (stop, pause,
  // reddit's backdrop) would squash rather than shrink.
  const bars = [
    ...node.querySelectorAll<SVGRectElement>('[data-slot="icon"] rect'),
  ].filter((bar) => bar.rx.baseVal.value * 2 === bar.width.baseVal.value)
  if (!bars.length || typeof bars[0].animate !== "function") {
    return
  }
  // One ripple per hover. Every bar has to be asked, not just the first: a
  // finished animation with no fill mode drops out of getAnimations, so by the
  // time the last bar is halfway the earliest ones report nothing.
  if (bars.some((bar) => bar.getAnimations().length > 0)) {
    return
  }
  bars.forEach((bar, i) => {
    // baseVal is the resting attribute, which an animation on the CSS property
    // leaves alone — so this stays correct mid-flight.
    const thickness = bar.width.baseVal.value
    const height = bar.height.baseVal.value
    const y = bar.y.baseVal.value
    const rest = { height: `${height}px`, y: `${y}px` }
    // Geometry rather than a transform, against the usual rule: scaleY squashes
    // a bar's rounded cap into a slab, and collapsing to a dot is the point. An
    // rx corner stays circular at any height. Costs a relayout of a 1em icon
    // for the length of one hover.
    bar.animate(
      [
        // Into the trough and out of it, so a bar passes through its minimum
        // rather than settling on it and holding the icon empty.
        { ...rest, easing: "ease-in" },
        {
          height: `${thickness}px`,
          y: `${y + (height - thickness) / 2}px`,
          offset: TROUGH_OFFSET,
          easing: "ease-out",
        },
        rest,
      ],
      { duration: DURATION_MS, delay: i * STAGGER_MS }
    )
  })
}

// A callback ref rather than a RefObject read from an effect: IconButton
// renders a tooltip trigger when enabled and a bare button when disabled, so
// React replaces the node whenever `disabled` flips. Only a callback ref is
// re-run on the replacement — an effect with [] deps would leave the listener
// on a detached node and the waveform would never ripple again.
export const audioLinesRippleRef: RefCallback<HTMLElement> = (node) => {
  if (!node) {
    return
  }
  const onPointerEnter = () => {
    // Hover has to be real: on a touch screen this would otherwise fire on the
    // tap that opens voice mode.
    if (window.matchMedia(MEDIA_QUERIES.canHover).matches) {
      rippleAudioLines(node)
    }
  }
  node.addEventListener("pointerenter", onPointerEnter)
  return () => node.removeEventListener("pointerenter", onPointerEnter)
}
